import React from "react";

const DailyPage = ({
  formData,
  handleChange,
  handleCheckboxChange,
  dtDisplay,
}) => {
  // 라디오 버튼 전용 핸들러 - 간단한 재클릭 해제 로직
  const handleRadioClick = (e) => {
    const { name, value } = e.target;

    // 현재 선택된 값과 클릭된 값이 같으면 해제, 다르면 선택
    if (formData[name] === value) {
      // 이미 선택된 상태라면 해제
      handleChange({ target: { name, value: "" } });
    } else {
      // 다른 값이라면 선택
      handleChange({ target: { name, value } });
    }
  };

  // 복사-붙여넣기 자동 포맷팅 핸들러
  const handlePaste = (e, fieldName) => {
    e.preventDefault(); // 기본 붙여넣기 동작 방지

    const pastedData = e.clipboardData.getData("text");
    const numbers = pastedData.replace(/\D/g, ""); // 숫자만 추출

    if (numbers.length <= 12) {
      // 자동 포맷팅 적용
      let formattedValue = numbers;

      if (numbers.length >= 4) {
        formattedValue = numbers.slice(0, 4) + "-" + numbers.slice(4);
      }
      if (numbers.length >= 6) {
        formattedValue =
          formattedValue.slice(0, 7) + "-" + formattedValue.slice(7);
      }
      if (numbers.length >= 8) {
        formattedValue =
          formattedValue.slice(0, 10) + " " + formattedValue.slice(10);
      }
      if (numbers.length >= 10) {
        formattedValue =
          formattedValue.slice(0, 13) + ":" + formattedValue.slice(13);
      }

      // 포맷된 값으로 필드 업데이트
      handleChange({
        target: { name: fieldName, value: formattedValue },
      });
    }
  };

  // TIL 총점 자동 계산 로직
  React.useEffect(() => {
    let result = 0;

    // Position Management
    const position = parseInt(formData.d1_til_position?.split(":")[0] || 0);
    const position_flat = parseInt(
      formData.d1_til_position_flat?.split(":")[0] || 0
    );
    result += Math.max(position, position_flat);

    // Sedation Management
    const sedation = parseInt(formData.d1_til_sedation?.split(":")[0] || 0);
    const sedation_higher =
      parseInt(formData.d1_til_sedation_higher?.split(":")[0] || 0) * 2;
    const sedation_suppression =
      parseInt(formData.d1_til_sedation_suppression?.split(":")[0] || 0) * 5;
    result += Math.max(sedation, sedation_higher, sedation_suppression);

    // Neuromuscular Blockade
    const nmb = parseInt(formData.d1_til_sedation_nmb?.split(":")[0] || 0) * 3;
    result += nmb;

    // CSF Drainage
    const csfdrain = parseInt(formData.d1_til_csfdrain?.split(":")[0] || 0);
    const csfdrainvol = parseInt(formData.d1_til_csfdrainvol || 0);
    if (csfdrain === 1 && csfdrainvol > 0) {
      result += csfdrainvol < 120 ? 2 : 3;
    }

    // Fluid and BP Management
    const fluidloading = parseInt(
      formData.d1_til_fluidloading?.split(":")[0] || 0
    );
    const vasopressor = parseInt(
      formData.d1_til_vasopressor?.split(":")[0] || 0
    );
    result += fluidloading + vasopressor;

    // Ventilation Management
    const hyperventilation = parseInt(
      formData.d1_til_hyperventilation?.split(":")[0] || 0
    );
    const hyperventilation_mod =
      parseInt(formData.d1_til_hyperventilation_mod?.split(":")[0] || 0) * 2;
    const hyperventilation_intense =
      parseInt(formData.d1_til_hyperventilation_intense?.split(":")[0] || 0) *
      4;
    result += Math.max(
      hyperventilation,
      hyperventilation_mod,
      hyperventilation_intense
    );

    // Osmolar Therapy - Mannitol
    const osm_mnt_low =
      parseInt(formData.d1_til_osm_mnt_low?.split(":")[0] || 0) * 2;
    const osm_mnt_high =
      parseInt(formData.d1_til_osm_mnt_high?.split(":")[0] || 0) * 3;
    result += Math.max(osm_mnt_low, osm_mnt_high);

    // Osmolar Therapy - Hypertonic Saline
    const osm_hts_low =
      parseInt(formData.d1_til_osm_hts_low?.split(":")[0] || 0) * 2;
    const osm_hts_high =
      parseInt(formData.d1_til_osm_hts_high?.split(":")[0] || 0) * 3;
    result += Math.max(osm_hts_low, osm_hts_high);

    // Temperature Management
    const fever = parseInt(formData.d1_til_fever?.split(":")[0] || 0);
    const ttm_mild = parseInt(formData.d1_til_ttm_mild?.split(":")[0] || 0) * 2;
    const ttm_moderate =
      parseInt(formData.d1_til_ttm_moderate?.split(":")[0] || 0) * 5;
    result += Math.max(fever, ttm_mild, ttm_moderate);

    // Surgery Management
    const icp_surgery =
      parseInt(formData.d1_til_icp_surgery?.split(":")[0] || 0) * 4;
    const icp_decomcrani =
      parseInt(formData.d1_til_icp_decomcrani?.split(":")[0] || 0) * 5;
    result += icp_surgery + icp_decomcrani;

    // 결과 업데이트
    handleChange({
      target: {
        name: "d1_til_summary",
        value: result.toString(),
      },
    });
  }, [
    formData.d1_til_position,
    formData.d1_til_position_flat,
    formData.d1_til_sedation,
    formData.d1_til_sedation_higher,
    formData.d1_til_sedation_suppression,
    formData.d1_til_sedation_nmb,
    formData.d1_til_csfdrain,
    formData.d1_til_csfdrainvol,
    formData.d1_til_fluidloading,
    formData.d1_til_vasopressor,
    formData.d1_til_hyperventilation,
    formData.d1_til_hyperventilation_mod,
    formData.d1_til_hyperventilation_intense,
    formData.d1_til_osm_mnt_low,
    formData.d1_til_osm_mnt_high,
    formData.d1_til_osm_hts_low,
    formData.d1_til_osm_hts_high,
    formData.d1_til_fever,
    formData.d1_til_ttm_mild,
    formData.d1_til_ttm_moderate,
    formData.d1_til_icp_surgery,
    formData.d1_til_icp_decomcrani,
  ]);

  // GCS 총점 자동 계산 로직
  React.useEffect(() => {
    const e_score = parseInt(formData.d1_gcs_worst_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d1_gcs_worst_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d1_gcs_worst_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d1_gcs_worst_total",
        value: result.toString(),
      },
    });
  }, [
    formData.d1_gcs_worst_e,
    formData.d1_gcs_worst_v,
    formData.d1_gcs_worst_m,
  ]);

  // GCS 최선값 총점 자동 계산 로직
  React.useEffect(() => {
    const e_score = parseInt(formData.d1_gcs_best_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d1_gcs_best_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d1_gcs_best_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d1_gcs_best_total",
        value: result.toString(),
      },
    });
  }, [formData.d1_gcs_best_e, formData.d1_gcs_best_v, formData.d1_gcs_best_m]);

  // GCS 최악값 총점 자동 계산 로직 (Daily2)
  React.useEffect(() => {
    const e_score = parseInt(formData.d2_gcs_worst_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d2_gcs_worst_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d2_gcs_worst_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d2_gcs_worst_total",
        value: result.toString(),
      },
    });
  }, [
    formData.d2_gcs_worst_e,
    formData.d2_gcs_worst_v,
    formData.d2_gcs_worst_m,
  ]);

  // GCS 최선값 총점 자동 계산 로직 (Daily2)
  React.useEffect(() => {
    const e_score = parseInt(formData.d2_gcs_best_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d2_gcs_best_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d2_gcs_best_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d2_gcs_best_total",
        value: result.toString(),
      },
    });
  }, [formData.d2_gcs_best_e, formData.d2_gcs_best_v, formData.d2_gcs_best_m]);

  // TIL 총점 자동 계산 로직 (Daily2)
  React.useEffect(() => {
    let result = 0;

    // Position Management
    const position = parseInt(formData.d2_til_position?.split(":")[0] || 0);
    const position_flat = parseInt(
      formData.d2_til_position_flat?.split(":")[0] || 0
    );
    result += Math.max(position, position_flat);

    // Sedation Management
    const sedation = parseInt(formData.d2_til_sedation?.split(":")[0] || 0);
    const sedation_higher =
      parseInt(formData.d2_til_sedation_higher?.split(":")[0] || 0) * 2;
    const sedation_suppression =
      parseInt(formData.d2_til_sedation_suppression?.split(":")[0] || 0) * 5;
    result += Math.max(sedation, sedation_higher, sedation_suppression);

    // Neuromuscular Blockade
    const nmb = parseInt(formData.d2_til_sedation_nmb?.split(":")[0] || 0) * 3;
    result += nmb;

    // CSF Drainage
    const csfdrain = parseInt(formData.d2_til_csfdrain?.split(":")[0] || 0);
    const csfdrainvol = parseInt(formData.d2_til_csfdrainvol || 0);
    if (csfdrain === 1 && csfdrainvol > 0) {
      result += csfdrainvol < 120 ? 2 : 3;
    }

    // Fluid and BP Management
    const fluidloading = parseInt(
      formData.d2_til_fluidloading?.split(":")[0] || 0
    );
    const vasopressor = parseInt(
      formData.d2_til_vasopressor?.split(":")[0] || 0
    );
    result += fluidloading + vasopressor;

    // Ventilation Management
    const hyperventilation = parseInt(
      formData.d2_til_hyperventilation?.split(":")[0] || 0
    );
    const hyperventilation_mod =
      parseInt(formData.d2_til_hyperventilation_mod?.split(":")[0] || 0) * 2;
    const hyperventilation_intense =
      parseInt(formData.d2_til_hyperventilation_intense?.split(":")[0] || 0) *
      4;
    result += Math.max(
      hyperventilation,
      hyperventilation_mod,
      hyperventilation_intense
    );

    // Osmolar Therapy - Mannitol
    const osm_mnt_low =
      parseInt(formData.d2_til_osm_mnt_low?.split(":")[0] || 0) * 2;
    const osm_mnt_high =
      parseInt(formData.d2_til_osm_mnt_high?.split(":")[0] || 0) * 3;
    result += Math.max(osm_mnt_low, osm_mnt_high);

    // Osmolar Therapy - Hypertonic Saline
    const osm_hts_low =
      parseInt(formData.d2_til_osm_hts_low?.split(":")[0] || 0) * 2;
    const osm_hts_high =
      parseInt(formData.d2_til_osm_hts_high?.split(":")[0] || 0) * 3;
    result += Math.max(osm_hts_low, osm_hts_high);

    // Temperature Management
    const fever = parseInt(formData.d2_til_fever?.split(":")[0] || 0);
    const ttm_mild = parseInt(formData.d2_til_ttm_mild?.split(":")[0] || 0) * 2;
    const ttm_moderate =
      parseInt(formData.d2_til_ttm_moderate?.split(":")[0] || 0) * 5;
    result += Math.max(fever, ttm_mild, ttm_moderate);

    // Surgery Management
    const icp_surgery =
      parseInt(formData.d2_til_icp_surgery?.split(":")[0] || 0) * 4;
    const icp_decomcrani =
      parseInt(formData.d2_til_icp_decomcrani?.split(":")[0] || 0) * 5;
    result += icp_surgery + icp_decomcrani;

    // 결과 업데이트
    handleChange({
      target: {
        name: "d2_til_summary",
        value: result.toString(),
      },
    });
  }, [
    formData.d2_til_position,
    formData.d2_til_position_flat,
    formData.d2_til_sedation,
    formData.d2_til_sedation_higher,
    formData.d2_til_sedation_suppression,
    formData.d2_til_sedation_nmb,
    formData.d2_til_csfdrain,
    formData.d2_til_csfdrainvol,
    formData.d2_til_fluidloading,
    formData.d2_til_vasopressor,
    formData.d2_til_hyperventilation,
    formData.d2_til_hyperventilation_mod,
    formData.d2_til_hyperventilation_intense,
    formData.d2_til_osm_mnt_low,
    formData.d2_til_osm_mnt_high,
    formData.d2_til_osm_hts_low,
    formData.d2_til_osm_hts_high,
    formData.d2_til_fever,
    formData.d2_til_ttm_mild,
    formData.d2_til_ttm_moderate,
    formData.d2_til_icp_surgery,
    formData.d2_til_icp_decomcrani,
  ]);

  // GCS 최악값 총점 자동 계산 로직 (Daily3)
  React.useEffect(() => {
    const e_score = parseInt(formData.d3_gcs_worst_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d3_gcs_worst_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d3_gcs_worst_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d3_gcs_worst_total",
        value: result.toString(),
      },
    });
  }, [
    formData.d3_gcs_worst_e,
    formData.d3_gcs_worst_v,
    formData.d3_gcs_worst_m,
  ]);

  // GCS 최선값 총점 자동 계산 로직 (Daily3)
  React.useEffect(() => {
    const e_score = parseInt(formData.d3_gcs_best_e?.split(":")[0] || 0);
    const v_score = parseInt(formData.d3_gcs_best_v?.split(":")[0] || 0);
    const m_score = parseInt(formData.d3_gcs_best_m?.split(":")[0] || 0);

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: "d3_gcs_best_total",
        value: result.toString(),
      },
    });
  }, [formData.d3_gcs_best_e, formData.d3_gcs_best_v, formData.d3_gcs_best_m]);

  // TIL 총점 자동 계산 로직 (Daily3)
  React.useEffect(() => {
    let total = 0;

    // 1. Position Management (최대값 적용)
    const position_score = Math.max(
      formData.d3_til_position === "1 : Yes" ? 1 : 0,
      formData.d3_til_position_flat === "1 : Yes" ? 1 : 0
    );
    total += position_score;

    // 2. Sedation Management (최대값 적용)
    const sedation_score = Math.max(
      formData.d3_til_sedation === "1 : Yes" ? 1 : 0,
      formData.d3_til_sedation_higher === "1 : Yes" ? 2 : 0,
      formData.d3_til_sedation_suppression === "1 : Yes" ? 5 : 0
    );
    total += sedation_score;

    // 3. Neuromuscular Blockade
    if (formData.d3_til_sedation_nmb === "1 : Yes") total += 3;

    // 4. CSF Drainage
    if (formData.d3_til_csfdrain === "1 : Yes") {
      const volume = parseFloat(formData.d3_til_csfdrainvol) || 0;
      total += volume >= 120 ? 3 : 2;
    }

    // 5. Fluid/BP Management (합산)
    if (formData.d3_til_fluidloading === "1 : Yes") total += 1;
    if (formData.d3_til_vasopressor === "1 : Yes") total += 1;

    // 6. Ventilation Management (최대값 적용)
    const ventilation_score = Math.max(
      formData.d3_til_hyperventilation === "1 : Yes" ? 1 : 0,
      formData.d3_til_hyperventilation_mod === "1 : Yes" ? 2 : 0,
      formData.d3_til_hyperventilation_intense === "1 : Yes" ? 4 : 0
    );
    total += ventilation_score;

    // 7. Osmolar Therapy (각각 최대값 적용)
    const mannitol_score = Math.max(
      formData.d3_til_osm_mnt_low === "1 : Yes" ? 2 : 0,
      formData.d3_til_osm_mnt_high === "1 : Yes" ? 3 : 0
    );
    const hts_score = Math.max(
      formData.d3_til_osm_hts_low === "1 : Yes" ? 2 : 0,
      formData.d3_til_osm_hts_high === "1 : Yes" ? 3 : 0
    );
    total += mannitol_score + hts_score;

    // 8. Temperature Management (최대값 적용)
    const temp_score = Math.max(
      formData.d3_til_fever === "1 : Yes" ? 1 : 0,
      formData.d3_til_ttm_mild === "1 : Yes" ? 2 : 0,
      formData.d3_til_ttm_moderate === "1 : Yes" ? 5 : 0
    );
    total += temp_score;

    // 9. Surgery Management (합산)
    if (formData.d3_til_icp_surgery === "1 : Yes") total += 4;
    if (formData.d3_til_icp_decomcrani === "1 : Yes") total += 5;

    handleChange({
      target: {
        name: "d3_til_summary",
        value: total.toString(),
      },
    });
  }, [
    formData.d3_til_position,
    formData.d3_til_position_flat,
    formData.d3_til_sedation,
    formData.d3_til_sedation_higher,
    formData.d3_til_sedation_suppression,
    formData.d3_til_sedation_nmb,
    formData.d3_til_csfdrain,
    formData.d3_til_csfdrainvol,
    formData.d3_til_fluidloading,
    formData.d3_til_vasopressor,
    formData.d3_til_hyperventilation,
    formData.d3_til_hyperventilation_mod,
    formData.d3_til_hyperventilation_intense,
    formData.d3_til_osm_mnt_low,
    formData.d3_til_osm_mnt_high,
    formData.d3_til_osm_hts_low,
    formData.d3_til_osm_hts_high,
    formData.d3_til_fever,
    formData.d3_til_ttm_mild,
    formData.d3_til_ttm_moderate,
    formData.d3_til_icp_surgery,
    formData.d3_til_icp_decomcrani,
  ]);

  return (
    <section className="vip-form-section">
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle">Daily 1</h3>

        {/* Daily 작성시 환자의 위치 */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Daily 작성시 환자의 위치 (d1_location)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["ICU", "Sub-ICU/Stroke Unit", "Ward"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_location"
                      value={opt}
                      checked={formData.d1_location === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Assessment Time and Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              time of worst GCS (d1_gcs_worst_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_gcs_worst_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d1_gcs_worst_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue =
                    dtDisplay(formData.d1_gcs_worst_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d1_gcs_worst_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Assessment conditions (d1_gcs_worst_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_asscond"
                      value={opt}
                      checked={formData.d1_gcs_worst_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Change over past 24 hours (d1_gcs_worst_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_change24h"
                      value={opt}
                      checked={formData.d1_gcs_worst_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d1_gcs_worst_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_change24h_cause"
                      value={opt}
                      checked={formData.d1_gcs_worst_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_e (d1_gcs_worst_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_e"
                      value={option.value}
                      checked={formData.d1_gcs_worst_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_v (d1_gcs_worst_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_v"
                      value={option.value}
                      checked={formData.d1_gcs_worst_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_m (d1_gcs_worst_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_worst_m"
                      value={option.value}
                      checked={formData.d1_gcs_worst_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_total (d1_gcs_worst_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_gcs_worst_total"
                value={formData.d1_gcs_worst_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pupil Assessment Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        {/* Pupil Symmetry */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_symmetry (d1_pupil_worst_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_worst_sym"
                      value={opt}
                      checked={formData.d1_pupil_worst_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_right (d1_pupil_worst_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_worst_lr_rt"
                      value={option.value}
                      checked={formData.d1_pupil_worst_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_right (d1_pupil_worst_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_pupil_worst_size_rt"
                value={formData.d1_pupil_worst_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_right (d1_pupil_worst_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_worst_unknown_rt"
                      value={opt}
                      checked={formData.d1_pupil_worst_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_left (d1_pupil_worst_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_pupil_worst_size_lt"
                value={formData.d1_pupil_worst_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_left (d1_pupil_worst_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_worst_lr_lt"
                      value={option.value}
                      checked={formData.d1_pupil_worst_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_left (d1_pupil_worst_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_worst_unknown_lt"
                      value={opt}
                      checked={formData.d1_pupil_worst_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best GCS Section */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS same as best GCS? (d1_gcs_best)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best"
                      value={opt}
                      checked={formData.d1_gcs_best === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              time of best GCS (d1_gcs_best_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_gcs_best_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d1_gcs_best_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d1_gcs_best_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d1_gcs_best_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
                onPaste={(e) => handlePaste(e, "d1_gcs_best_dt")}
              />
            </div>
          </div>
        </div>

        {/* Best GCS Assessment Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS Assessment conditions (d1_gcs_best_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_asscond"
                      value={opt}
                      checked={formData.d1_gcs_best_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              GCS Change over past 24 hours (d1_gcs_best_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_change24h"
                      value={opt}
                      checked={formData.d1_gcs_best_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d1_gcs_best_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_change24h_cause"
                      value={opt}
                      checked={formData.d1_gcs_best_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Best GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_e (d1_gcs_best_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_e"
                      value={option.value}
                      checked={formData.d1_gcs_best_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_v (d1_gcs_best_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_v"
                      value={option.value}
                      checked={formData.d1_gcs_best_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_m (d1_gcs_best_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_gcs_best_m"
                      value={option.value}
                      checked={formData.d1_gcs_best_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_total (d1_gcs_best_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_gcs_best_total"
                value={formData.d1_gcs_best_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>

        {/* Best Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_symmetry (d1_pupil_best_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_best_sym"
                      value={opt}
                      checked={formData.d1_pupil_best_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_right (d1_pupil_best_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_best_lr_rt"
                      value={option.value}
                      checked={formData.d1_pupil_best_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_right (d1_pupil_best_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_pupil_best_size_rt"
                value={formData.d1_pupil_best_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_right (d1_pupil_best_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_best_unknown_rt"
                      value={opt}
                      checked={formData.d1_pupil_best_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_left (d1_pupil_best_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_best_lr_lt"
                      value={option.value}
                      checked={formData.d1_pupil_best_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_left (d1_pupil_best_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_pupil_best_size_lt"
                value={formData.d1_pupil_best_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_left (d1_pupil_best_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_pupil_best_unknown_lt"
                      value={opt}
                      checked={formData.d1_pupil_best_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* Neurological Worsening Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 여부 (d1_neuroworse)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_neuroworse"
                      value={opt}
                      checked={formData.d1_neuroworse === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 종류 (d1_neuroworse_type)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "GCS total 2점 이상 악화될 경우",
                  "Development of pupillary abnormalities",
                  "Other neurological and/or CT deterioration",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d1_neuroworse_type"
                      value={opt}
                      checked={formData.d1_neuroworse_type?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />

                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 대처 (d1_neuroworse_action)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "None",
                  "Unscheduled CT scan",
                  "Change in medical therapy",
                  "Surgical intervention",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d1_neuroworse_action"
                      value={opt}
                      checked={formData.d1_neuroworse_action?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />
                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* TIL (Therapeutic Intensity Level) Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Therapeutic Intensity Level (TIL) 체크 일시 (d1_til_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_til_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d1_til_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d1_til_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d1_til_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
                onPaste={(e) => handlePaste(e, "d1_til_dt")}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Form Completion Status (d1_til_complete)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Not Started",
                  "In Process",
                  "Complete",
                  "Queries Outstanding",
                  "Not Performed",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_complete"
                      value={opt}
                      checked={formData.d1_til_complete === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Position Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Head elevation for ICP control (d1_til_position)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_position"
                      value={opt.value}
                      checked={formData.d1_til_position === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Nursed flat (180°) for CPP management (d1_til_position_flat)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_position_flat"
                      value={opt.value}
                      checked={formData.d1_til_position_flat === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Sedation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Sedation (low dose as required for mechanical ventilation)
              (d1_til_sedation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_sedation"
                      value={opt.value}
                      checked={formData.d1_til_sedation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Higher dose sedation for ICP control (not aiming for burst
              supression) (d1_til_sedation_higher)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_sedation_higher"
                      value={opt.value}
                      checked={formData.d1_til_sedation_higher === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Metabolic suppression for ICP control with high dose barbiturates
              or propofol (d1_til_sedation_suppression)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_sedation_suppression"
                      value={opt.value}
                      checked={
                        formData.d1_til_sedation_suppression === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Neuromuscular blockade (paralysis) (d1_til_sedation_nmb)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_sedation_nmb"
                      value={opt.value}
                      checked={formData.d1_til_sedation_nmb === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CSF Drainage */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              CSF Drainage (d1_til_csfdrain)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_csfdrain"
                      value={opt.value}
                      checked={formData.d1_til_csfdrain === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Volume drained (ml) (d1_til_csfdrainvol)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d1_til_csfdrainvol"
                value={formData.d1_til_csfdrainvol || ""}
                onChange={handleChange}
              />
              <span className="unit-text">ml/day</span>
            </div>
          </div>
        </div>

        {/* Fluid Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Fluid loading for maintenance of cerebral perfusion
              (d1_til_fluidloading)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_fluidloading"
                      value={opt.value}
                      checked={formData.d1_til_fluidloading === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Vasopressor therapy required for management of cerebral perfusion
              (d1_til_vasopressor)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_vasopressor"
                      value={opt.value}
                      checked={formData.d1_til_vasopressor === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ventilation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Mild hypocapnia for ICP control [PaCO2 ≤35 and {"<"} 40]
              (d1_til_hyperventilation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_hyperventilation"
                      value={opt.value}
                      checked={formData.d1_til_hyperventilation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Moderate hypocapnia for ICP control [PaCO2 ≤30 and {"<"} 35]
              (d1_til_hyperventilation_mod)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_hyperventilation_mod"
                      value={opt.value}
                      checked={
                        formData.d1_til_hyperventilation_mod === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Intensive hypocapnia for ICP control [PaCO2 ({"<"} 30 mmHg)]
              (d1_til_hyperventilation_intense)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_hyperventilation_intense"
                      value={opt.value}
                      checked={
                        formData.d1_til_hyperventilation_intense === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Osmotic Therapy */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with mannitol up to 2 g/kg/24 hours
              (d1_til_osm_mnt_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_osm_mnt_low"
                      value={opt.value}
                      checked={formData.d1_til_osm_mnt_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with hypertonic saline up to 0.3 g/kg/24
              hours (d1_til_osm_hts_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d1_til_osm_hts_low"
                      value={opt.value}
                      checked={formData.d1_til_osm_hts_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* High Dose Osmolar Therapy */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with mannitol {">"} 2 g/kg/24 hours
            (d1_til_osm_mnt_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_osm_mnt_high"
                    value={opt.value}
                    checked={formData.d1_til_osm_mnt_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with hypertonic saline {">"} 0.3 g/kg/24 hours
            (d1_til_osm_hts_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_osm_hts_high"
                    value={opt.value}
                    checked={formData.d1_til_osm_hts_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Treatment of fever (temperature {">"} 38℃) or spontaneous
            hypothermia (temperature {"<"} 34.5℃) (d1_til_fever)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_fever"
                    value={opt.value}
                    checked={formData.d1_til_fever === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Mild hypothermia for ICP control with a lower limit of 35°C
            (d1_til_ttm_mild)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_ttm_mild"
                    value={opt.value}
                    checked={formData.d1_til_ttm_mild === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hypothermia below 35°C (d1_til_ttm_moderate)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_ttm_moderate"
                    value={opt.value}
                    checked={formData.d1_til_ttm_moderate === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Surgery Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Intracranial operation for progressive mass lesion, not scheduled on
            admission (d1_til_icp_surgery)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_icp_surgery"
                    value={opt.value}
                    checked={formData.d1_til_icp_surgery === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Decompressive craniectomy (d1_til_icp_decomcrani)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_icp_decomcrani"
                    value={opt.value}
                    checked={formData.d1_til_icp_decomcrani === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Change Reason */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Reason for change in treatment over this day (d1_til_change_reason)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No change", value: "No change" },
                {
                  label: "Intensified: Clinical deterioration",
                  value: "Intensified: Clinical deterioration",
                },
                {
                  label:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                  value:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                },
                {
                  label: "Intensified:Increased ICP (documented)",
                  value: "Intensified:Increased ICP (documented)",
                },
                {
                  label:
                    "Intensified:Clinical decision to target other mechanism",
                  value:
                    "Intensified:Clinical decision to target other mechanism",
                },
                {
                  label: "Intensified:Change of doctor (different shift)",
                  value: "Intensified:Change of doctor (different shift)",
                },
                {
                  label: "Decreasing:Clinical improvement",
                  value: "Decreasing:Clinical improvement",
                },
                {
                  label: "Decreasing:Adequate control over ICP",
                  value: "Decreasing:Adequate control over ICP",
                },
                {
                  label: "Decreasing:Upper treatment limit reached/past",
                  value: "Decreasing:Upper treatment limit reached/past",
                },
                {
                  label: "Decreasing:Further treatment considered futile",
                  value: "Decreasing:Further treatment considered futile",
                },
                {
                  label: "Decreasing:Change of doctor (different shift)",
                  value: "Decreasing:Change of doctor (different shift)",
                },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d1_til_change_reason"
                    value={option.value}
                    checked={formData.d1_til_change_reason === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TIL Summary */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Therapeutic Intensity Level 총점 (d1_til_summary)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【TIL Score 자동계산 공식】
                <br />
                <br />
                1. Position Management (최대값 적용)
                <br />• 두부 거상: 1점
                <br />• 평면 자세: 1점
                <br />
                <br />
                2. Sedation Management (최대값 적용)
                <br />• 기본 진정: 1점
                <br />• 고용량 진정: 2점
                <br />• 발작 억제: 5점
                <br />
                <br />
                3. Neuromuscular Blockade
                <br />• 근이완제: 3점
                <br />
                <br />
                4. CSF Drainage
                <br />• {"<"}120ml/day: 2점
                <br />• ≥120ml/day: 3점
                <br />
                <br />
                5. Fluid/BP Management (합산)
                <br />• 수액 부하: 1점
                <br />• 승압제: 1점
                <br />
                <br />
                6. Ventilation (최대값 적용)
                <br />• 경도 과환기: 1점
                <br />• 중등도 과환기: 2점
                <br />• 심한 과환기: 4점
                <br />
                <br />
                7. Osmolar Therapy (각각 최대값 적용)
                <br />• Mannitol 저용량: 2점
                <br />• Mannitol 고용량: 3점
                <br />• HTS 저용량: 2점
                <br />• HTS 고용량: 3점
                <br />
                <br />
                8. Temperature Management (최대값 적용)
                <br />• 발열 관리: 1점
                <br />• 경도 저체온: 2점
                <br />• 중등도 저체온: 5점
                <br />
                <br />
                9. Surgery (합산)
                <br />• 두개내 수술: 4점
                <br />• 감압 두개절제술: 5점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name="d1_til_summary"
              value={formData.d1_til_summary || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
        <div className="block-2column" />
      </div>

      <div className="basic-info-fieldset">
        <h3 className="section-subtitle">Daily 2</h3>

        {/* Daily 작성시 환자의 위치 */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Daily 작성시 환자의 위치 (d2_location)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["ICU", "Sub-ICU/Stroke Unit", "Ward"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_location"
                      value={opt}
                      checked={formData.d2_location === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Assessment Time and Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              time of worst GCS (d2_gcs_worst_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_gcs_worst_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d2_gcs_worst_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue =
                    dtDisplay(formData.d2_gcs_worst_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d2_gcs_worst_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Assessment conditions (d2_gcs_worst_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_asscond"
                      value={opt}
                      checked={formData.d2_gcs_worst_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Change over past 24 hours (d2_gcs_worst_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_change24h"
                      value={opt}
                      checked={formData.d2_gcs_worst_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d2_gcs_worst_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_change24h_cause"
                      value={opt}
                      checked={formData.d2_gcs_worst_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_e (d2_gcs_worst_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_e"
                      value={option.value}
                      checked={formData.d2_gcs_worst_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_v (d2_gcs_worst_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_v"
                      value={option.value}
                      checked={formData.d2_gcs_worst_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_m (d2_gcs_worst_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_worst_m"
                      value={option.value}
                      checked={formData.d2_gcs_worst_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_total (d2_gcs_worst_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_gcs_worst_total"
                value={formData.d2_gcs_worst_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pupil Assessment Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        {/* Pupil Symmetry */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_symmetry (d2_pupil_worst_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_worst_sym"
                      value={opt}
                      checked={formData.d2_pupil_worst_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_right (d2_pupil_worst_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_worst_lr_rt"
                      value={option.value}
                      checked={formData.d2_pupil_worst_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_right (d2_pupil_worst_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_pupil_worst_size_rt"
                value={formData.d2_pupil_worst_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_right (d2_pupil_worst_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_worst_unknown_rt"
                      value={opt}
                      checked={formData.d2_pupil_worst_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_left (d2_pupil_worst_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_pupil_worst_size_lt"
                value={formData.d2_pupil_worst_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_left (d2_pupil_worst_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_worst_lr_lt"
                      value={option.value}
                      checked={formData.d2_pupil_worst_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_left (d2_pupil_worst_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_worst_unknown_lt"
                      value={opt}
                      checked={formData.d2_pupil_worst_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best GCS Section */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS same as best GCS? (d2_gcs_best)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best"
                      value={opt}
                      checked={formData.d2_gcs_best === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              time of best GCS (d2_gcs_best_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_gcs_best_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d2_gcs_best_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d2_gcs_best_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d2_gcs_best_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Best GCS Assessment Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS Assessment conditions (d2_gcs_best_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_asscond"
                      value={opt}
                      checked={formData.d2_gcs_best_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              GCS Change over past 24 hours (d2_gcs_best_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_change24h"
                      value={opt}
                      checked={formData.d2_gcs_best_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d2_gcs_best_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_change24h_cause"
                      value={opt}
                      checked={formData.d2_gcs_best_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Best GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_e (d2_gcs_best_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_e"
                      value={option.value}
                      checked={formData.d2_gcs_best_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_v (d2_gcs_best_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_v"
                      value={option.value}
                      checked={formData.d2_gcs_best_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_m (d2_gcs_best_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_gcs_best_m"
                      value={option.value}
                      checked={formData.d2_gcs_best_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_total (d2_gcs_best_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_gcs_best_total"
                value={formData.d2_gcs_best_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>

        {/* Best Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_symmetry (d2_pupil_best_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_best_sym"
                      value={opt}
                      checked={formData.d2_pupil_best_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_right (d2_pupil_best_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_best_lr_rt"
                      value={option.value}
                      checked={formData.d2_pupil_best_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_right (d2_pupil_best_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_pupil_best_size_rt"
                value={formData.d2_pupil_best_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_right (d2_pupil_best_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_best_unknown_rt"
                      value={opt}
                      checked={formData.d2_pupil_best_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_left (d2_pupil_best_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_best_lr_lt"
                      value={option.value}
                      checked={formData.d2_pupil_best_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_left (d2_pupil_best_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_pupil_best_size_lt"
                value={formData.d2_pupil_best_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_left (d2_pupil_best_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_pupil_best_unknown_lt"
                      value={opt}
                      checked={formData.d2_pupil_best_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* Neurological Worsening Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 여부 (d2_neuroworse)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_neuroworse"
                      value={opt}
                      checked={formData.d2_neuroworse === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 종류 (d2_neuroworse_type)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "GCS total 2점 이상 악화될 경우",
                  "Development of pupillary abnormalities",
                  "Other neurological and/or CT deterioration",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d2_neuroworse_type"
                      value={opt}
                      checked={formData.d2_neuroworse_type?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />

                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 대처 (d2_neuroworse_action)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "None",
                  "Unscheduled CT scan",
                  "Change in medical therapy",
                  "Surgical intervention",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d2_neuroworse_action"
                      value={opt}
                      checked={formData.d2_neuroworse_action?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />
                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* TIL (Therapeutic Intensity Level) Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Therapeutic Intensity Level (TIL) 체크 일시 (d2_til_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_til_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d2_til_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d2_til_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d2_til_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
                onPaste={(e) => handlePaste(e, "d2_til_dt")}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Form Completion Status (d2_til_complete)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Not Started",
                  "In Process",
                  "Complete",
                  "Queries Outstanding",
                  "Not Performed",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_complete"
                      value={opt}
                      checked={formData.d2_til_complete === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Position Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Head elevation for ICP control (d2_til_position)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_position"
                      value={opt.value}
                      checked={formData.d2_til_position === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Nursed flat (180°) for CPP management (d2_til_position_flat)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_position_flat"
                      value={opt.value}
                      checked={formData.d2_til_position_flat === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Sedation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Sedation (low dose as required for mechanical ventilation)
              (d2_til_sedation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_sedation"
                      value={opt.value}
                      checked={formData.d2_til_sedation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Higher dose sedation for ICP control (not aiming for burst
              supression) (d2_til_sedation_higher)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_sedation_higher"
                      value={opt.value}
                      checked={formData.d2_til_sedation_higher === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Metabolic suppression for ICP control with high dose barbiturates
              or propofol (d2_til_sedation_suppression)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_sedation_suppression"
                      value={opt.value}
                      checked={
                        formData.d2_til_sedation_suppression === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Neuromuscular blockade (paralysis) (d2_til_sedation_nmb)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_sedation_nmb"
                      value={opt.value}
                      checked={formData.d2_til_sedation_nmb === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CSF Drainage */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              CSF Drainage (d2_til_csfdrain)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_csfdrain"
                      value={opt.value}
                      checked={formData.d2_til_csfdrain === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Volume drained (ml) (d2_til_csfdrainvol)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d2_til_csfdrainvol"
                value={formData.d2_til_csfdrainvol || ""}
                onChange={handleChange}
              />
              <span className="unit-text">ml/day</span>
            </div>
          </div>
        </div>

        {/* Fluid Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Fluid loading for maintenance of cerebral perfusion
              (d2_til_fluidloading)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_fluidloading"
                      value={opt.value}
                      checked={formData.d2_til_fluidloading === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Vasopressor therapy required for management of cerebral perfusion
              (d2_til_vasopressor)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_vasopressor"
                      value={opt.value}
                      checked={formData.d2_til_vasopressor === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ventilation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Mild hypocapnia for ICP control [PaCO2 ≤35 and {"<"} 40]
              (d2_til_hyperventilation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_hyperventilation"
                      value={opt.value}
                      checked={formData.d2_til_hyperventilation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Moderate hypocapnia for ICP control [PaCO2 ≤30 and {"<"} 35]
              (d2_til_hyperventilation_mod)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_hyperventilation_mod"
                      value={opt.value}
                      checked={
                        formData.d2_til_hyperventilation_mod === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Intensive hypocapnia for ICP control [PaCO2 ({"<"} 30 mmHg)]
              (d2_til_hyperventilation_intense)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_hyperventilation_intense"
                      value={opt.value}
                      checked={
                        formData.d2_til_hyperventilation_intense === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Osmotic Therapy */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with mannitol up to 2 g/kg/24 hours
              (d2_til_osm_mnt_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_osm_mnt_low"
                      value={opt.value}
                      checked={formData.d2_til_osm_mnt_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with hypertonic saline up to 0.3 g/kg/24
              hours (d2_til_osm_hts_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d2_til_osm_hts_low"
                      value={opt.value}
                      checked={formData.d2_til_osm_hts_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* High Dose Osmolar Therapy */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with mannitol {">"} 2 g/kg/24 hours
            (d2_til_osm_mnt_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_osm_mnt_high"
                    value={opt.value}
                    checked={formData.d2_til_osm_mnt_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with hypertonic saline {">"} 0.3 g/kg/24 hours
            (d2_til_osm_hts_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_osm_hts_high"
                    value={opt.value}
                    checked={formData.d2_til_osm_hts_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Treatment of fever (temperature {">"} 38℃) or spontaneous
            hypothermia (temperature {"<"} 34.5℃) (d2_til_fever)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_fever"
                    value={opt.value}
                    checked={formData.d2_til_fever === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Mild hypothermia for ICP control with a lower limit of 35°C
            (d2_til_ttm_mild)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_ttm_mild"
                    value={opt.value}
                    checked={formData.d2_til_ttm_mild === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hypothermia below 35°C (d2_til_ttm_moderate)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_ttm_moderate"
                    value={opt.value}
                    checked={formData.d2_til_ttm_moderate === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Surgery Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Intracranial operation for progressive mass lesion, not scheduled on
            admission (d2_til_icp_surgery)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_icp_surgery"
                    value={opt.value}
                    checked={formData.d2_til_icp_surgery === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Decompressive craniectomy (d2_til_icp_decomcrani)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_icp_decomcrani"
                    value={opt.value}
                    checked={formData.d2_til_icp_decomcrani === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Change Reason */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Reason for change in treatment over this day (d2_til_change_reason)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No change", value: "No change" },
                {
                  label: "Intensified: Clinical deterioration",
                  value: "Intensified: Clinical deterioration",
                },
                {
                  label:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                  value:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                },
                {
                  label: "Intensified:Increased ICP (documented)",
                  value: "Intensified:Increased ICP (documented)",
                },
                {
                  label:
                    "Intensified:Clinical decision to target other mechanism",
                  value:
                    "Intensified:Clinical decision to target other mechanism",
                },
                {
                  label: "Intensified:Change of doctor (different shift)",
                  value: "Intensified:Change of doctor (different shift)",
                },
                {
                  label: "Decreasing:Clinical improvement",
                  value: "Decreasing:Clinical improvement",
                },
                {
                  label: "Decreasing:Adequate control over ICP",
                  value: "Decreasing:Adequate control over ICP",
                },
                {
                  label: "Decreasing:Upper treatment limit reached/past",
                  value: "Decreasing:Upper treatment limit reached/past",
                },
                {
                  label: "Decreasing:Further treatment considered futile",
                  value: "Decreasing:Further treatment considered futile",
                },
                {
                  label: "Decreasing:Change of doctor (different shift)",
                  value: "Decreasing:Change of doctor (different shift)",
                },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d2_til_change_reason"
                    value={option.value}
                    checked={formData.d2_til_change_reason === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TIL Summary */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Therapeutic Intensity Level 총점 (d2_til_summary)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【TIL Score 자동계산 공식】
                <br />
                <br />
                1. Position Management (최대값 적용)
                <br />• 두부 거상: 1점
                <br />• 평면 자세: 1점
                <br />
                <br />
                2. Sedation Management (최대값 적용)
                <br />• 기본 진정: 1점
                <br />• 고용량 진정: 2점
                <br />• 발작 억제: 5점
                <br />
                <br />
                3. Neuromuscular Blockade
                <br />• 근이완제: 3점
                <br />
                <br />
                4. CSF Drainage
                <br />• {"<"}120ml/day: 2점
                <br />• ≥120ml/day: 3점
                <br />
                <br />
                5. Fluid/BP Management (합산)
                <br />• 수액 부하: 1점
                <br />• 승압제: 1점
                <br />
                <br />
                6. Ventilation (최대값 적용)
                <br />• 경도 과환기: 1점
                <br />• 중등도 과환기: 2점
                <br />• 심한 과환기: 4점
                <br />
                <br />
                7. Osmolar Therapy (각각 최대값 적용)
                <br />• Mannitol 저용량: 2점
                <br />• Mannitol 고용량: 3점
                <br />• HTS 저용량: 2점
                <br />• HTS 고용량: 3점
                <br />
                <br />
                8. Temperature Management (최대값 적용)
                <br />• 발열 관리: 1점
                <br />• 경도 저체온: 2점
                <br />• 중등도 저체온: 5점
                <br />
                <br />
                9. Surgery (합산)
                <br />• 두개내 수술: 4점
                <br />• 감압 두개절제술: 5점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name="d2_til_summary"
              value={formData.d2_til_summary || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
        <div className="block-2column" />
      </div>

      <div className="basic-info-fieldset">
        <h3 className="section-subtitle">Daily 3</h3>

        {/* Daily 작성시 환자의 위치 */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Daily 작성시 환자의 위치 (d3_location)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["ICU", "Sub-ICU/Stroke Unit", "Ward"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_location"
                      value={opt}
                      checked={formData.d3_location === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Assessment Time and Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              time of worst GCS (d3_gcs_worst_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_gcs_worst_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d3_gcs_worst_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue =
                    dtDisplay(formData.d3_gcs_worst_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d3_gcs_worst_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Assessment conditions (d3_gcs_worst_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_asscond"
                      value={opt}
                      checked={formData.d3_gcs_worst_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS Change over past 24 hours (d3_gcs_worst_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_change24h"
                      value={opt}
                      checked={formData.d3_gcs_worst_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d3_gcs_worst_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_change24h_cause"
                      value={opt}
                      checked={formData.d3_gcs_worst_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_e (d3_gcs_worst_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_e"
                      value={option.value}
                      checked={formData.d3_gcs_worst_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_v (d3_gcs_worst_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_v"
                      value={option.value}
                      checked={formData.d3_gcs_worst_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_m (d3_gcs_worst_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_worst_m"
                      value={option.value}
                      checked={formData.d3_gcs_worst_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS_total (d3_gcs_worst_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_gcs_worst_total"
                value={formData.d3_gcs_worst_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pupil Assessment Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        {/* Pupil Symmetry */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_symmetry (d3_pupil_worst_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_worst_sym"
                      value={opt}
                      checked={formData.d3_pupil_worst_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_right (d3_pupil_worst_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_worst_lr_rt"
                      value={option.value}
                      checked={formData.d3_pupil_worst_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_right (d3_pupil_worst_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_pupil_worst_size_rt"
                value={formData.d3_pupil_worst_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_right (d3_pupil_worst_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_worst_unknown_rt"
                      value={opt}
                      checked={formData.d3_pupil_worst_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_size_left (d3_pupil_worst_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_pupil_worst_size_lt"
                value={formData.d3_pupil_worst_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_left (d3_pupil_worst_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_worst_lr_lt"
                      value={option.value}
                      checked={formData.d3_pupil_worst_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst pupil_reflex_unknown_left (d3_pupil_worst_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_worst_unknown_lt"
                      value={opt}
                      checked={formData.d3_pupil_worst_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best GCS Section */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Worst GCS same as best GCS? (d3_gcs_best)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best"
                      value={opt}
                      checked={formData.d3_gcs_best === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              time of best GCS (d3_gcs_best_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_gcs_best_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d3_gcs_best_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d3_gcs_best_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d3_gcs_best_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Best GCS Assessment Conditions */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS Assessment conditions (d3_gcs_best_asscond)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No sedation or paralysis",
                  "Sedated",
                  "Paralyzed",
                  "Temporary stop of sedation/paralysis",
                  "Reversal of sedation/paralysis",
                  "Active reversal (pharmacologic) of sedation/paralysis",
                  "Other",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_asscond"
                      value={opt}
                      checked={formData.d3_gcs_best_asscond === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "12px" }}>
                      {opt}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* GCS Change and Cause */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              GCS Change over past 24 hours (d3_gcs_best_change24h)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "No change",
                  "Improving",
                  "Episode of deterioration",
                  "Sustained deterioration",
                  "Fluctuating",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_change24h"
                      value={opt}
                      checked={formData.d3_gcs_best_change24h === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Cause of change (d3_gcs_best_change24h_cause)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Mainly intracranial",
                  "Mainly extracranial",
                  "Both to an equal extent",
                  "Unknown",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_change24h_cause"
                      value={opt}
                      checked={formData.d3_gcs_best_change24h_cause === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Best GCS Components */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_e (d3_gcs_best_e)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  { label: "2 : To pain", value: "2 : To pain" },
                  { label: "3 : To speech", value: "3 : To speech" },
                  { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                  {
                    label: "Untestable (swollen)",
                    value: "0 : Untestable (swollen)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_e"
                      value={option.value}
                      checked={formData.d3_gcs_best_e === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_v (d3_gcs_best_v)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Incomprehensible sound",
                    value: "2 : Incomprehensible sound",
                  },
                  {
                    label: "3 : Inappropriate words",
                    value: "3 : Inappropriate words",
                  },
                  { label: "4 : Confused", value: "4 : Confused" },
                  { label: "5 : Oriented", value: "5 : Oriented" },
                  {
                    label: "Untestable (tracheotomy/endotracheal tube)",
                    value: "0 : Untestable (tracheotomy/endotracheal tube)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_v"
                      value={option.value}
                      checked={formData.d3_gcs_best_v === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_m (d3_gcs_best_m)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "1 : None", value: "1 : None" },
                  {
                    label: "2 : Abnormal extension",
                    value: "2 : Abnormal extension",
                  },
                  {
                    label: "3 : Abnormal flexion",
                    value: "3 : Abnormal flexion",
                  },
                  {
                    label: "4 : Normal flexion/withdrawal",
                    value: "4 : Normal flexion/withdrawal",
                  },
                  {
                    label: "5 : Localizes to pain",
                    value: "5 : Localizes to pain",
                  },
                  {
                    label: "6 : Obeys command",
                    value: "6 : Obeys command",
                  },
                  {
                    label: "Untestable (Deep sedation/paralyzed)",
                    value: "0 : Untestable (Deep sedation/paralyzed)",
                  },
                  {
                    label: "Untestable (other)",
                    value: "0 : Untestable (Other)",
                  },
                  { label: "Unknown", value: "0 : Unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_gcs_best_m"
                      value={option.value}
                      checked={formData.d3_gcs_best_m === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text" style={{ fontSize: "13px" }}>
                      {option.label}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best GCS_total (d3_gcs_best_total)
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">
                  【GCS Total Score 자동계산 공식】
                  <br />
                  <br />
                  ★ 필수 입력 필드 (3개):
                  <br />• GCS_E (Eye 반응)
                  <br />• GCS_V (Verbal 반응)
                  <br />• GCS_M (Motor 반응)
                  <br />
                  <br />
                  ★ 계산 로직:
                  <br />
                  1) E=0 또는 M=0인 경우
                  <br />
                  &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                  <br />
                  <br />
                  2) V=0인 경우 (기관삽관 등):
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                  <br />
                  &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                  <br />
                  <br />
                  3) 그 외의 경우:
                  <br />
                  &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                  <br />
                  <br />★ 점수 범위: 0~15점
                </span>
              </span>
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_gcs_best_total"
                value={formData.d3_gcs_best_total || ""}
                readOnly
                style={{
                  backgroundColor: "#f5f5f5",
                  cursor: "not-allowed",
                  fontWeight: "bold",
                  color: "#007bff",
                }}
              />
            </div>
          </div>
        </div>

        {/* Best Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_symmetry (d3_pupil_best_sym)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_best_sym"
                      value={opt}
                      checked={formData.d3_pupil_best_sym === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Right Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_right (d3_pupil_best_lr_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_best_lr_rt"
                      value={option.value}
                      checked={formData.d3_pupil_best_lr_rt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_right (d3_pupil_best_size_rt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_pupil_best_size_rt"
                value={formData.d3_pupil_best_size_rt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_right (d3_pupil_best_unknown_rt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_best_unknown_rt"
                      value={opt}
                      checked={formData.d3_pupil_best_unknown_rt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        {/* Best Left Pupil Assessment */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_left (d3_pupil_best_lr_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "+ (Brisk)", value: "(Brisk) +" },
                  { label: "+ (Sluggish)", value: "(Sluggish) +" },
                  { label: "- (Negative)", value: "(Negative) -" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_best_lr_lt"
                      value={option.value}
                      checked={formData.d3_pupil_best_lr_lt === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}} // 빈 함수로 React 경고 방지
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_size_left (d3_pupil_best_size_lt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_pupil_best_size_lt"
                value={formData.d3_pupil_best_size_lt || ""}
                onChange={handleChange}
              />
              <span className="unit-text">mm</span>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Best pupil_reflex_unknown_left (d3_pupil_best_unknown_lt)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["Untestable", "Unknown"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_pupil_best_unknown_lt"
                      value={opt}
                      checked={formData.d3_pupil_best_unknown_lt === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* Neurological Worsening Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 여부 (d3_neuroworse)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_neuroworse"
                      value={opt}
                      checked={formData.d3_neuroworse === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 종류 (d3_neuroworse_type)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "GCS total 2점 이상 악화될 경우",
                  "Development of pupillary abnormalities",
                  "Other neurological and/or CT deterioration",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d3_neuroworse_type"
                      value={opt}
                      checked={formData.d3_neuroworse_type?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />

                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 대처 (d3_neuroworse_action)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "None",
                  "Unscheduled CT scan",
                  "Change in medical therapy",
                  "Surgical intervention",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name="d3_neuroworse_action"
                      value={opt}
                      checked={formData.d3_neuroworse_action?.includes(opt)}
                      onChange={handleCheckboxChange}
                    />
                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* TIL (Therapeutic Intensity Level) Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Therapeutic Intensity Level (TIL) 체크 일시 (d3_til_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_til_dt"
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData.d3_til_dt)}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue = dtDisplay(formData.d3_til_dt) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: "d3_til_dt",
                        value: formattedValue,
                      },
                    });
                  }
                }}
                onPaste={(e) => handlePaste(e, "d3_til_dt")}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Form Completion Status (d3_til_complete)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Not Started",
                  "In Process",
                  "Complete",
                  "Queries Outstanding",
                  "Not Performed",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_complete"
                      value={opt}
                      checked={formData.d3_til_complete === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Position Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Head elevation for ICP control (d3_til_position)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_position"
                      value={opt.value}
                      checked={formData.d3_til_position === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Nursed flat (180°) for CPP management (d3_til_position_flat)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_position_flat"
                      value={opt.value}
                      checked={formData.d3_til_position_flat === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Sedation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Sedation (low dose as required for mechanical ventilation)
              (d3_til_sedation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_sedation"
                      value={opt.value}
                      checked={formData.d3_til_sedation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Higher dose sedation for ICP control (not aiming for burst
              supression) (d3_til_sedation_higher)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_sedation_higher"
                      value={opt.value}
                      checked={formData.d3_til_sedation_higher === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Metabolic suppression for ICP control with high dose barbiturates
              or propofol (d3_til_sedation_suppression)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_sedation_suppression"
                      value={opt.value}
                      checked={
                        formData.d3_til_sedation_suppression === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Neuromuscular blockade (paralysis) (d3_til_sedation_nmb)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_sedation_nmb"
                      value={opt.value}
                      checked={formData.d3_til_sedation_nmb === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CSF Drainage */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              CSF Drainage (d3_til_csfdrain)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_csfdrain"
                      value={opt.value}
                      checked={formData.d3_til_csfdrain === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Volume drained (ml) (d3_til_csfdrainvol)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name="d3_til_csfdrainvol"
                value={formData.d3_til_csfdrainvol || ""}
                onChange={handleChange}
              />
              <span className="unit-text">ml/day</span>
            </div>
          </div>
        </div>

        {/* Fluid Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Fluid loading for maintenance of cerebral perfusion
              (d3_til_fluidloading)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_fluidloading"
                      value={opt.value}
                      checked={formData.d3_til_fluidloading === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Vasopressor therapy required for management of cerebral perfusion
              (d3_til_vasopressor)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_vasopressor"
                      value={opt.value}
                      checked={formData.d3_til_vasopressor === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ventilation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Mild hypocapnia for ICP control [PaCO2 ≤35 and {"<"} 40]
              (d3_til_hyperventilation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_hyperventilation"
                      value={opt.value}
                      checked={formData.d3_til_hyperventilation === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Moderate hypocapnia for ICP control [PaCO2 ≤30 and {"<"} 35]
              (d3_til_hyperventilation_mod)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_hyperventilation_mod"
                      value={opt.value}
                      checked={
                        formData.d3_til_hyperventilation_mod === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Intensive hypocapnia for ICP control [PaCO2 ({"<"} 30 mmHg)]
              (d3_til_hyperventilation_intense)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_hyperventilation_intense"
                      value={opt.value}
                      checked={
                        formData.d3_til_hyperventilation_intense === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Osmotic Therapy */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with mannitol up to 2 g/kg/24 hours
              (d3_til_osm_mnt_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_osm_mnt_low"
                      value={opt.value}
                      checked={formData.d3_til_osm_mnt_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with hypertonic saline up to 0.3 g/kg/24
              hours (d3_til_osm_hts_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="d3_til_osm_hts_low"
                      value={opt.value}
                      checked={formData.d3_til_osm_hts_low === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* High Dose Osmolar Therapy */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with mannitol {">"} 2 g/kg/24 hours
            (d3_til_osm_mnt_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_osm_mnt_high"
                    value={opt.value}
                    checked={formData.d3_til_osm_mnt_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with hypertonic saline {">"} 0.3 g/kg/24 hours
            (d3_til_osm_hts_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_osm_hts_high"
                    value={opt.value}
                    checked={formData.d3_til_osm_hts_high === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Treatment of fever (temperature {">"} 38℃) or spontaneous
            hypothermia (temperature {"<"} 34.5℃) (d3_til_fever)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_fever"
                    value={opt.value}
                    checked={formData.d3_til_fever === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Mild hypothermia for ICP control with a lower limit of 35°C
            (d3_til_ttm_mild)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_ttm_mild"
                    value={opt.value}
                    checked={formData.d3_til_ttm_mild === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hypothermia below 35°C (d3_til_ttm_moderate)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_ttm_moderate"
                    value={opt.value}
                    checked={formData.d3_til_ttm_moderate === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Surgery Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Intracranial operation for progressive mass lesion, not scheduled on
            admission (d3_til_icp_surgery)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_icp_surgery"
                    value={opt.value}
                    checked={formData.d3_til_icp_surgery === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Decompressive craniectomy (d3_til_icp_decomcrani)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_icp_decomcrani"
                    value={opt.value}
                    checked={formData.d3_til_icp_decomcrani === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Change Reason */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Reason for change in treatment over this day (d3_til_change_reason)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No change", value: "No change" },
                {
                  label: "Intensified: Clinical deterioration",
                  value: "Intensified: Clinical deterioration",
                },
                {
                  label:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                  value:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                },
                {
                  label: "Intensified:Increased ICP (documented)",
                  value: "Intensified:Increased ICP (documented)",
                },
                {
                  label:
                    "Intensified:Clinical decision to target other mechanism",
                  value:
                    "Intensified:Clinical decision to target other mechanism",
                },
                {
                  label: "Intensified:Change of doctor (different shift)",
                  value: "Intensified:Change of doctor (different shift)",
                },
                {
                  label: "Decreasing:Clinical improvement",
                  value: "Decreasing:Clinical improvement",
                },
                {
                  label: "Decreasing:Adequate control over ICP",
                  value: "Decreasing:Adequate control over ICP",
                },
                {
                  label: "Decreasing:Upper treatment limit reached/past",
                  value: "Decreasing:Upper treatment limit reached/past",
                },
                {
                  label: "Decreasing:Further treatment considered futile",
                  value: "Decreasing:Further treatment considered futile",
                },
                {
                  label: "Decreasing:Change of doctor (different shift)",
                  value: "Decreasing:Change of doctor (different shift)",
                },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="d3_til_change_reason"
                    value={option.value}
                    checked={formData.d3_til_change_reason === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TIL Summary */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Therapeutic Intensity Level 총점 (d3_til_summary)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【TIL Score 자동계산 공식】
                <br />
                <br />
                1. Position Management (최대값 적용)
                <br />• 두부 거상: 1점
                <br />• 평면 자세: 1점
                <br />
                <br />
                2. Sedation Management (최대값 적용)
                <br />• 기본 진정: 1점
                <br />• 고용량 진정: 2점
                <br />• 발작 억제: 5점
                <br />
                <br />
                3. Neuromuscular Blockade
                <br />• 근이완제: 3점
                <br />
                <br />
                4. CSF Drainage
                <br />• {"<"}120ml/day: 2점
                <br />• ≥120ml/day: 3점
                <br />
                <br />
                5. Fluid/BP Management (합산)
                <br />• 수액 부하: 1점
                <br />• 승압제: 1점
                <br />
                <br />
                6. Ventilation (최대값 적용)
                <br />• 경도 과환기: 1점
                <br />• 중등도 과환기: 2점
                <br />• 심한 과환기: 4점
                <br />
                <br />
                7. Osmolar Therapy (각각 최대값 적용)
                <br />• Mannitol 저용량: 2점
                <br />• Mannitol 고용량: 3점
                <br />• HTS 저용량: 2점
                <br />• HTS 고용량: 3점
                <br />
                <br />
                8. Temperature Management (최대값 적용)
                <br />• 발열 관리: 1점
                <br />• 경도 저체온: 2점
                <br />• 중등도 저체온: 5점
                <br />
                <br />
                9. Surgery (합산)
                <br />• 두개내 수술: 4점
                <br />• 감압 두개절제술: 5점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name="d3_til_summary"
              value={formData.d3_til_summary || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
        <div className="block-2column" />
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Completion status_3 (daily_status)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "Not started", value: "Not started" },
                { label: "In process", value: "In process" },
                { label: "Complete", value: "Complete" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name="daily_status"
                    value={option.value}
                    checked={formData.daily_status === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}} // 빈 함수로 React 경고 방지
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>
    </section>
  );
};

export default DailyPage;
